/**
 * SecureKeyService
 * 
 * Provides secure handling of API keys and sensitive credentials through base64 obfuscation.
 * This service ensures that sensitive keys are not exposed in plain text in the source code.
 */

// Obfuscated keys (base64 encoded)
const OBFUSCATED_KEYS = {
  // Supabase keys - these are base64 encoded values of the actual keys
  // In a real implementation, replace these with actual encoded values
  SUPABASE_URL: btoa(import.meta.env.VITE_SUPABASE_URL || ''),
  SUPABASE_ANON_KEY: btoa(import.meta.env.VITE_SUPABASE_ANON_KEY || ''),
  
  // API URL
  API_URL: btoa(import.meta.env.VITE_API_URL || ''),
  
  // Admin credentials
  ADMIN_EMAIL: btoa(import.meta.env.VITE_ADMIN_EMAIL || ''),
  ADMIN_PASSWORD: btoa(import.meta.env.VITE_ADMIN_PASSWORD || ''),
};

// Key storage in localStorage (for session persistence)
const KEY_STORAGE_PREFIX = 'secure_key_';

/**
 * SecureKeyService class for handling sensitive API keys
 */
export class SecureKeyService {
  private static instance: SecureKeyService;
  private decodedKeys: Record<string, string> = {};

  private constructor() {
    // Private constructor to enforce singleton pattern
  }

  /**
   * Get the singleton instance of SecureKeyService
   */
  public static getInstance(): SecureKeyService {
    if (!SecureKeyService.instance) {
      SecureKeyService.instance = new SecureKeyService();
    }
    return SecureKeyService.instance;
  }

  /**
   * Encode a string to base64
   * @param value String to encode
   * @returns Base64 encoded string
   */
  public encode(value: string): string {
    try {
      return btoa(value);
    } catch (error) {
      console.error('Error encoding value:', error);
      return '';
    }
  }

  /**
   * Decode a base64 string
   * @param encoded Base64 encoded string
   * @returns Decoded string
   */
  public decode(encoded: string): string {
    try {
      return atob(encoded);
    } catch (error) {
      console.error('Error decoding value:', error);
      return '';
    }
  }

  /**
   * Get a key by its name, with fallback to environment variables
   * @param keyName Name of the key to retrieve
   * @returns The decoded key value
   */
  public getKey(keyName: keyof typeof OBFUSCATED_KEYS): string {
    // First check if we already decoded this key
    if (this.decodedKeys[keyName]) {
      return this.decodedKeys[keyName];
    }

    // Then check if it's in localStorage (from a previous session)
    const storedKey = localStorage.getItem(`${KEY_STORAGE_PREFIX}${keyName}`);
    if (storedKey) {
      this.decodedKeys[keyName] = storedKey;
      return storedKey;
    }

    // Then try to decode from our obfuscated keys
    if (OBFUSCATED_KEYS[keyName]) {
      const decodedKey = this.decode(OBFUSCATED_KEYS[keyName]);
      if (decodedKey) {
        // Store for future use
        this.decodedKeys[keyName] = decodedKey;
        localStorage.setItem(`${KEY_STORAGE_PREFIX}${keyName}`, decodedKey);
        return decodedKey;
      }
    }

    // Fallback to environment variables
    const envKey = import.meta.env[`VITE_${keyName}`];
    if (envKey) {
      // Store for future use
      this.decodedKeys[keyName] = envKey;
      localStorage.setItem(`${KEY_STORAGE_PREFIX}${keyName}`, envKey);
      return envKey;
    }

    // Special handling for admin credentials - make them optional
    if (keyName === 'ADMIN_EMAIL' || keyName === 'ADMIN_PASSWORD') {
      console.warn(`Admin credential ${keyName} not found, but continuing`);
      return '';  // Return empty string instead of redirecting
    }
    
    // For other missing keys, log error and redirect
    console.error(`Key not found: ${keyName}`);
    this.redirectToErrorPage();
    return '';
  }

  /**
   * Get the Supabase URL
   * @returns Decoded Supabase URL
   */
  public getSupabaseUrl(): string {
    return this.getKey('SUPABASE_URL');
  }

  /**
   * Get the Supabase anonymous key
   * @returns Decoded Supabase anonymous key
   */
  public getSupabaseAnonKey(): string {
    return this.getKey('SUPABASE_ANON_KEY');
  }

  /**
   * Get the API URL
   * @returns Decoded API URL
   */
  public getApiUrl(): string {
    return this.getKey('API_URL');
  }

  /**
   * Mask a sensitive key for logging (shows only last 4 characters)
   * @param key The key to mask
   * @returns Masked key
   */
  public maskKey(key: string): string {
    if (!key || key.length <= 4) {
      return '****';
    }
    return `****${key.slice(-4)}`;
  }

  /**
   * Clear all stored keys from memory and localStorage
   */
  public clearKeys(): void {
    this.decodedKeys = {};
    
    // Clear from localStorage
    Object.keys(OBFUSCATED_KEYS).forEach(key => {
      localStorage.removeItem(`${KEY_STORAGE_PREFIX}${key}`);
    });
  }
  
  /**
   * Redirect to the appropriate error page based on the current URL path
   */
  private redirectToErrorPage(): void {
    // Determine which error page to show based on the current URL
    if (window.location.pathname.includes('/charity')) {
      window.location.href = '/auth/CharityServiceError';
    } else {
      window.location.href = '/auth/BusinessServiceError';
    }
  }
}

// Export singleton instance
export const secureKeyService = SecureKeyService.getInstance();

/**
 * Helper function to encode keys for development
 * This can be used in the console to generate encoded values for OBFUSCATED_KEYS
 * Example: encodeForDevelopment('your-api-key-here')
 */
export const encodeForDevelopment = (key: string): string => {
  return btoa(key);
};
